import { ActionTree } from 'vuex';
import {
  deviceSearchApiCaller,
  deviceObuTrackerApiCaller,
  deviceStateApiCaller
} from './callers';

import {
  DevicesState,
  DEVICES_LIST_NAVIGATION,
  UPDATE_DEVICES_LIST_STATE,
  QUERY_NUMBER_OF_ACTIVE_DEVICES,
  QUERY_DEVICES_LAST_POSITIONS,
  SEARCH_DEVICES,
  USER_SELECTION_CLEAR_DEVICES,
  USER_SELECTION_REGISTER_DEVICE,
  TRIGGER_DEVICE_USER_SELECTION_ACTION,
  USER_SELECTION_RESET_ACTIONS,
  UPDATE_NAVIGATION,
  REINIT,
  REQUEST_CHANGE_DEVICE_STATE_OBU,
  REQUEST_CHANGE_DEVICE_STATE_VDO,
  FETCH_SUGGESTIONS,
  QUERY_DEVICES_HISTORY,
  QUERY_DEVICES_TOLL_DOMAINS_NAMES,
  SAVE_DEVICE_FILTERS
} from './types';
import { HistorySearchApiCaller } from '../history/callers';

const actions: ActionTree<DevicesState, Record<string, never>> = {
  [REINIT]({ commit }): void {
    commit(REINIT);
  },
  [UPDATE_NAVIGATION]({ commit }, payload): void {
    commit(UPDATE_NAVIGATION, payload);
  },
  [DEVICES_LIST_NAVIGATION]({ commit }, payload): void {
    commit(DEVICES_LIST_NAVIGATION, payload);
  },
  [USER_SELECTION_REGISTER_DEVICE]({ commit }, payload): void {
    commit(USER_SELECTION_REGISTER_DEVICE, payload);
  },
  [USER_SELECTION_CLEAR_DEVICES]({ commit }): void {
    commit(USER_SELECTION_CLEAR_DEVICES);
  },
  [USER_SELECTION_RESET_ACTIONS]({ commit }): void {
    commit(USER_SELECTION_RESET_ACTIONS);
  },
  [TRIGGER_DEVICE_USER_SELECTION_ACTION]({ commit }, payload): void {
    commit(TRIGGER_DEVICE_USER_SELECTION_ACTION, payload);
  },
  [QUERY_NUMBER_OF_ACTIVE_DEVICES]({ commit, rootGetters }) {
    const master = rootGetters['user/getSelectedMasterAccount'];
    commit(UPDATE_DEVICES_LIST_STATE, {
      view: 'numberOfActiveDevices',
      stateKey: 'loading',
      stateValue: true
    });

    return deviceSearchApiCaller.getActiveDevicesCount(master.id)
      .then((activeDevices) => {
        commit(UPDATE_DEVICES_LIST_STATE, {
          view: 'numberOfActiveDevices',
          stateKey: 'fetched',
          stateValue: true
        });

        commit(UPDATE_DEVICES_LIST_STATE, {
          view: 'numberOfActiveDevices',
          stateKey: 'error',
          stateValue: false
        });

        commit(QUERY_NUMBER_OF_ACTIVE_DEVICES, {
          numberOfActiveDevices: activeDevices
        });

        commit(UPDATE_DEVICES_LIST_STATE, {
          view: 'numberOfActiveDevices',
          stateKey: 'loading',
          stateValue: false
        });
      })
      .catch(() => {
        commit(UPDATE_DEVICES_LIST_STATE, {
          view: 'numberOfActiveDevices',
          stateKey: 'error',
          stateValue: true
        });
        commit(UPDATE_DEVICES_LIST_STATE, {
          view: 'numberOfActiveDevices',
          stateKey: 'loading',
          stateValue: false
        });
      });
  },

  async [SEARCH_DEVICES]({ commit, dispatch }, payload) {
    commit(UPDATE_DEVICES_LIST_STATE, {
      view: 'devicesList',
      stateKey: 'loading',
      stateValue: true
    });
    const {
      devicesPerPage: size = 20, // devicesListNavigation.ts
      start = 0,
      filters = {}
    } = {
      ...payload
    };
    return deviceSearchApiCaller.searchDevices(filters, payload.masterId, start, size)
      .then((res) => {
        commit(UPDATE_DEVICES_LIST_STATE, {
          view: 'devicesList',
          stateKey: 'error',
          stateValue: false
        });
        commit(SEARCH_DEVICES, res);
        commit(UPDATE_DEVICES_LIST_STATE, {
          view: 'devicesList',
          stateKey: 'fetched',
          stateValue: true
        });
      })
      .catch(() => {
        commit(UPDATE_DEVICES_LIST_STATE, {
          view: 'devicesList',
          stateKey: 'error',
          stateValue: true
        });
      })
      .finally(async () => {
        await dispatch(FETCH_SUGGESTIONS);
        commit(UPDATE_DEVICES_LIST_STATE, {
          view: 'devicesList',
          stateKey: 'loading',
          stateValue: false
        });
      });
  },
  [QUERY_DEVICES_LAST_POSITIONS]({ rootGetters }, payload) {
    const master = rootGetters['user/getSelectedMasterAccount'];
    const {
      start = undefined,
      end = undefined,
      deviceId = ''
    } = {
      ...payload
    };

    return deviceObuTrackerApiCaller.getLastPositions(master.id, deviceId, start, end);
  },
  [REQUEST_CHANGE_DEVICE_STATE_OBU]({ commit }, payload) {
    commit(UPDATE_DEVICES_LIST_STATE, {
      view: 'tollingUpdateState',
      stateKey: 'loading',
      stateValue: true
    });
    return deviceStateApiCaller
      .sendObuCommands(payload)
      .then((response) => {
        commit(UPDATE_DEVICES_LIST_STATE, {
          view: 'tollingUpdateState',
          stateKey: 'fetched',
          stateValue: true
        });
        commit(UPDATE_DEVICES_LIST_STATE, {
          view: 'tollingUpdateState',
          stateKey: 'loading',
          stateValue: false
        });
        return response;
      })
      .catch((error) => {
        commit(UPDATE_DEVICES_LIST_STATE, {
          view: 'tollingUpdateState',
          stateKey: 'error',
          stateValue: true
        });
        commit(UPDATE_DEVICES_LIST_STATE, {
          view: 'tollingUpdateState',
          stateKey: 'loading',
          stateValue: false
        });
        throw error.json();
      });
  },
  [REQUEST_CHANGE_DEVICE_STATE_VDO]({ commit }, payload) {
    commit(UPDATE_DEVICES_LIST_STATE, {
      view: 'tollingUpdateState',
      stateKey: 'loading',
      stateValue: true
    });
    return deviceStateApiCaller
      .sendVdoCommands(payload)
      .then((response) => {
        commit(UPDATE_DEVICES_LIST_STATE, {
          view: 'tollingUpdateState',
          stateKey: 'fetched',
          stateValue: true
        });
        commit(UPDATE_DEVICES_LIST_STATE, {
          view: 'tollingUpdateState',
          stateKey: 'loading',
          stateValue: false
        });
        return response;
      })
      .catch((error) => {
        commit(UPDATE_DEVICES_LIST_STATE, {
          view: 'tollingUpdateState',
          stateKey: 'error',
          stateValue: true
        });
        commit(UPDATE_DEVICES_LIST_STATE, {
          view: 'tollingUpdateState',
          stateKey: 'loading',
          stateValue: false
        });
        throw error.json();
      });
  },
  [FETCH_SUGGESTIONS]({ commit, rootGetters }) {
    const master = rootGetters['user/getSelectedMasterAccount'];
    return deviceSearchApiCaller.getSuggestions(master.id)
      .then((res) => {
        commit(FETCH_SUGGESTIONS, res);
      });
  },
  [QUERY_DEVICES_HISTORY]({ commit, rootGetters }, payload: any) {
    const master = rootGetters['user/getSelectedMasterAccount'];
    commit(UPDATE_DEVICES_LIST_STATE, {
      view: 'devicesHistory',
      stateKey: 'loading',
      stateValue: {
        deviceId: payload.deviceId,
        state: true
      }
    });
    commit(UPDATE_DEVICES_LIST_STATE, {
      view: 'devicesHistory',
      stateKey: 'fetched',
      stateValue: {
        deviceId: payload.deviceId,
        state: false
      }
    });
    commit(UPDATE_DEVICES_LIST_STATE, {
      view: 'devicesHistory',
      stateKey: 'error',
      stateValue: {
        deviceId: payload.deviceId,
        state: false
      }
    });
    return HistorySearchApiCaller.searchHistory(payload.size, payload.start, master.id, [payload.deviceId], undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, payload.highPriority || undefined)
      .then((res) => {
        commit(QUERY_DEVICES_HISTORY, { deviceId: payload.deviceId, history: res.history });
        commit(UPDATE_DEVICES_LIST_STATE, {
          view: 'devicesHistory',
          stateKey: 'loading',
          stateValue: {
            deviceId: payload.deviceId,
            state: false
          }
        });
        commit(UPDATE_DEVICES_LIST_STATE, {
          view: 'devicesHistory',
          stateKey: 'fetched',
          stateValue: {
            deviceId: payload.deviceId,
            state: true
          }
        });
        commit(UPDATE_DEVICES_LIST_STATE, {
          view: 'devicesHistory',
          stateKey: 'error',
          stateValue: {
            deviceId: payload.deviceId,
            state: false
          }
        });
      }).catch(() => {
        commit(UPDATE_DEVICES_LIST_STATE, {
          view: 'devicesHistory',
          stateKey: 'loading',
          stateValue: {
            deviceId: payload.deviceId,
            state: false
          }
        });
        commit(UPDATE_DEVICES_LIST_STATE, {
          view: 'devicesHistory',
          stateKey: 'fetched',
          stateValue: {
            deviceId: payload.deviceId,
            state: false
          }
        });
        commit(UPDATE_DEVICES_LIST_STATE, {
          view: 'devicesHistory',
          stateKey: 'error',
          stateValue: {
            deviceId: payload.deviceId,
            state: true
          }
        });
      });
  },
  [QUERY_DEVICES_TOLL_DOMAINS_NAMES]({ commit, rootGetters }, payload: { persoTargetId: string, persoTargetType: any, masterId: number, subscriptions: { ruleNameId: number, tolldomainId: number }[] }) {
    const domains = rootGetters['tollDomain/getTollDomains'];
    const tolldomainIds = payload.subscriptions.map((sub: any) => sub.tolldomainId);
    const subscribed = payload.subscriptions.map((sub: any) => sub.ruleNameId);
    const deviceDomains = domains.filter((domain: any) => tolldomainIds.includes(domain.id));
    // show only the subscribed ruled names
    deviceDomains.forEach((domain: any) => { domain.ruleConfigNames = domain.ruleConfigNames.filter((rule: any) => subscribed.includes(rule.id)); });
    commit(QUERY_DEVICES_TOLL_DOMAINS_NAMES, { deviceID: payload.persoTargetId, masterId: payload.masterId, tolldomainNames: deviceDomains });
  },
  [SAVE_DEVICE_FILTERS](_, payload: { selectedFilters: string[], multiSearch: string }) {
    sessionStorage.removeItem('savedFilters');
    sessionStorage.setItem('savedFilters', JSON.stringify(payload));
  }
};

export default actions;
